/**
 * Double-precision 3-vector mirroring Carbon's Vector3d semantics.
 * Mutable, with in-place ops matching the C++ call patterns so the
 * integrator reproduces Destiny trajectories bit-for-bit in float64.
 */
export class Vec3 {
  constructor(
    public x = 0,
    public y = 0,
    public z = 0,
  ) {}

  clone(): Vec3 {
    return new Vec3(this.x, this.y, this.z);
  }

  set(x: number, y: number, z: number): this {
    this.x = x;
    this.y = y;
    this.z = z;
    return this;
  }

  copy(v: Vec3): this {
    this.x = v.x;
    this.y = v.y;
    this.z = v.z;
    return this;
  }

  add(v: Vec3): this {
    this.x += v.x;
    this.y += v.y;
    this.z += v.z;
    return this;
  }

  sub(v: Vec3): this {
    this.x -= v.x;
    this.y -= v.y;
    this.z -= v.z;
    return this;
  }

  scale(s: number): this {
    this.x *= s;
    this.y *= s;
    this.z *= s;
    return this;
  }

  lengthSq(): number {
    return this.x * this.x + this.y * this.y + this.z * this.z;
  }

  length(): number {
    return Math.sqrt(this.lengthSq());
  }

  dot(v: Vec3): number {
    return this.x * v.x + this.y * v.y + this.z * v.z;
  }

  /** In-place cross product: this = this × v (matches Vector3d::Cross). */
  cross(v: Vec3): this {
    const x = this.y * v.z - this.z * v.y;
    const y = this.z * v.x - this.x * v.z;
    const z = this.x * v.y - this.y * v.x;
    this.x = x;
    this.y = y;
    this.z = z;
    return this;
  }

  /** In-place normalize (no-op on zero vector, like Vector3d::Normalize). */
  normalize(): this {
    const len = this.length();
    if (len > 0) {
      this.x /= len;
      this.y /= len;
      this.z /= len;
    }
    return this;
  }
}

export const vsub = (a: Vec3, b: Vec3): Vec3 => new Vec3(a.x - b.x, a.y - b.y, a.z - b.z);
export const vadd = (a: Vec3, b: Vec3): Vec3 => new Vec3(a.x + b.x, a.y + b.y, a.z + b.z);
export const vscale = (a: Vec3, s: number): Vec3 => new Vec3(a.x * s, a.y * s, a.z * s);
